import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { contentTrackingAPIClient } from '@/marketing/infrastructure/influencer/campaign/contentTracking/contentTrackingAPIClient';
import { invalidateContentQueries } from './contentTrackingCache';

const REFRESH_PAGE_LIMIT = 10;

export interface RefreshProgress {
  processed: number;
  refreshed: number;
}

// Loops the paged refresh until the server runs out of creators; each page reads connected accounts, so this is slow by design.
export function useRefreshContentTrackingMetrics(campaignId: string) {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<RefreshProgress | null>(null);

  const refresh = async () => {
    if (progress) {
      return;
    }
    const totals: RefreshProgress = { processed: 0, refreshed: 0 };
    setProgress(totals);
    try {
      let cursor: string | undefined;
      do {
        const result = await contentTrackingAPIClient.refreshMetrics(campaignId, {
          limit: REFRESH_PAGE_LIMIT,
          ...(cursor ? { cursor } : {}),
        });
        totals.processed += result.processed;
        totals.refreshed += result.refreshed;
        setProgress({ ...totals });
        cursor = result.pageInfo.nextCursor ?? undefined;
      } while (cursor);
      toast.success(`Metrics refreshed for ${totals.refreshed} of ${totals.processed} creators`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Metrics refresh failed');
    } finally {
      await invalidateContentQueries(queryClient);
      setProgress(null);
    }
  };

  return { progress, refresh };
}
